import { data, useFetcher } from 'react-router';
import { authSessionStorage } from '~/shell/session.server';
import { decodeJwt, type JWTPayload } from 'jose';
import { getLogger } from '../../logging/logger';
import { useCallback, useEffect, useState } from 'react';
import { formatDate, getLocale } from '~/common/utils/dateUtil';
import { Button } from '~/common/components/ui/button';
import { namedAction } from 'remix-utils/named-action';
import { FORM_FIELDS } from '~/common/constants/formfields';
import {
  getAccessTokenFromContext,
  getIsOciSessionOnContext,
  getOptionalLoginInfo,
  getRefreshTokenFromContext
} from '~/shell/globalContext.ts';
import { getAuthSessionFromContext } from '~/shell/middleware/sessionMiddleware.ts';
import { keycloakLogout } from '~/shell/oauth.server.ts';
import { refreshOAuthTokens } from '~/shell/middleware/globalStorageMiddleware.ts';
import { SESSION_KEY_ACCESS_TOKEN, SESSION_KEY_REFRESH_TOKEN } from '~/shell/sessions.ts';

const log = getLogger('auth-session-route');

type TokenInformation = {
  expiration: Date;
};

function parseTokenInformation(token: string): TokenInformation {
  const decodedToken = decodeJwt<JWTPayload>(token);
  const expirationTime = new Date((decodedToken.exp ?? 1) * 1000);
  return {
    expiration: expirationTime
  };
}

export async function loader() {
  const loginInfo = getOptionalLoginInfo();

  let accessTokenInfo: TokenInformation | undefined;
  let refreshTokenInformation: TokenInformation | undefined;
  let isOciSession = false;

  try {
    if (loginInfo) {
      accessTokenInfo = parseTokenInformation(getAccessTokenFromContext());
      refreshTokenInformation = parseTokenInformation(getRefreshTokenFromContext());
      isOciSession = getIsOciSessionOnContext();
    }
  } catch (e) {
    log.error('Error parsing token information', e);
  }

  return {
    accessTokenInfo,
    refreshTokenInformation,
    loginInfo,
    isOciSession
  };
}

export async function action({ context, request }) {
  const loginInfo = getOptionalLoginInfo();

  return namedAction(await request.formData(), {
    async refresh() {
      const sessionContext = getAuthSessionFromContext(context);

      if (!loginInfo) {
        const headers = new Headers();
        headers.append('Set-Cookie', await authSessionStorage.destroySession(sessionContext));
        return data(null, { headers });
      } else {
        const currentRefreshToken = getRefreshTokenFromContext();
        const refreshedTokens = await refreshOAuthTokens(currentRefreshToken);
        const accessToken = refreshedTokens.access_token;
        // Keycloak rotiert den Refresh-Token; fehlt er in der Antwort, bleibt der bisherige gültig.
        const refreshToken = refreshedTokens.refresh_token ?? currentRefreshToken;
        // Update the session so the new token is persisted in the response Set-Cookie header
        sessionContext.set(SESSION_KEY_ACCESS_TOKEN, accessToken);
        sessionContext.set(SESSION_KEY_REFRESH_TOKEN, refreshToken);

        return data({
          accessTokenInfo: parseTokenInformation(accessToken),
          refreshTokenInformation: parseTokenInformation(refreshToken)
        });
      }
    },
    async deleteSessionCookie() {
      const sessionContext = getAuthSessionFromContext(context);
      const headers = new Headers();
      headers.append('Set-Cookie', await authSessionStorage.destroySession(sessionContext));
      return data(null, { headers });
    },
    async revokeAccessToken() {
      if (loginInfo) {
        await keycloakLogout();
      }
      return data(null);
    }
  });
}

export function SessionInformation() {
  const actionFetcher = useFetcher();
  const { data, load } = useFetcher<typeof loader>();

  useEffect(() => {
    load(`/infra/auth/session`);

    const intervalId = setInterval(() => {
      load(`/infra/auth/session`);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [load]);

  if (!data?.loginInfo) {
    return null;
  }

  const isSubmitting = actionFetcher.state === 'submitting';

  const loginInfo = data.loginInfo;
  const filteredCustomerValues = Object.fromEntries(Object.entries(loginInfo).filter(([key]) => key !== 'settings'));

  return (
    <div className={'flex gap-8'}>
      <div className={'flex gap-4 flex-col'}>
        <h2>Token Informationen</h2>
        <TokenInformationInfos title={'Access Token'} tokenInformation={data?.accessTokenInfo} />
        <TokenInformationInfos title={'Refresh Token'} tokenInformation={data?.refreshTokenInformation} />
        <div>
          <strong>OCI Session:</strong>
          <span className={'text-sm ml-2'}>{data?.isOciSession ? 'true' : 'false'}</span>
        </div>
        <actionFetcher.Form method={'post'} action={`/infra/auth/session`} className={'flex flex-col gap-2 my-4'}>
          <Button disabled={isSubmitting} name={FORM_FIELDS.Intent} value={'refresh'} type="submit">
            Refresh Tokens
          </Button>
          <Button disabled={isSubmitting} name={FORM_FIELDS.Intent} value={'deleteSessionCookie'} type="submit">
            Delete Session Cookie
          </Button>
          <Button disabled={isSubmitting} name={FORM_FIELDS.Intent} value={'revokeAccessToken'} type="submit">
            Logout w/o redirect
          </Button>
        </actionFetcher.Form>
      </div>
      <div>
        <h2>Customer</h2>
        <div>
          <div>
            {Object.entries(filteredCustomerValues).map(([key, value]) => (
              <div key={key} className={'flex gap-4'}>
                <div className={'text-sm min-w-[180px]'}>{key}</div>
                <div className={'text-sm'}>{JSON.stringify(value)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TokenInformationInfos({
  title,
  tokenInformation
}: Readonly<{
  title: string;
  tokenInformation?: TokenInformation;
}>) {
  return (
    <div>
      <strong>{title}</strong>
      <p className={'text-sm'}>
        Expiration:{' '}
        {tokenInformation ? (
          <span>{formatDate(tokenInformation?.expiration, 'dd.MM.yyyy HH:MM:ss', getLocale('de'))}</span>
        ) : (
          <span>No token</span>
        )}
      </p>
      <Countdown targetDate={tokenInformation?.expiration ?? new Date()} />
    </div>
  );
}

const Countdown = ({ targetDate }: { targetDate: Date }) => {
  const calculateTimeLeft = useCallback(() => {
    const difference = targetDate.getTime() - Date.now();
    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / (1000 * 60)) % 60);
      const seconds = Math.floor((difference / 1000) % 60);
      return { days, hours, minutes, seconds };
    }
    return null;
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return (
    <div className={'text-xs'}>
      {timeLeft ? (
        <p>{`${timeLeft.days} Tage, ${timeLeft.hours}:${timeLeft.minutes}:${timeLeft.seconds}`}</p>
      ) : (
        <p>Abgelaufen!</p>
      )}
    </div>
  );
};
